import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { albumService } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { loadBulkCache, userColor, userInitial } from '../albumData';

const POLL_INTERVAL_MS = 30_000;

function tradeUserFromId(bulkCache, userId) {
  const cached = bulkCache?.albums?.[userId];
  return {
    id: userId,
    name: cached?.n || `Usuario ${userId}`,
    initial: userInitial(cached?.n),
    color: userColor(userId),
  };
}

function mapTrade(t, myUserId, bulkCache) {
  const iAmProposer = t.proposerId === myUserId;
  const otherUserId = iAmProposer ? t.receiverId : t.proposerId;
  const youGive = iAmProposer ? (t.offered || []) : (t.requested || []);
  const youReceive = iAmProposer ? (t.requested || []) : (t.offered || []);
  return {
    id: t.id,
    direction: iAmProposer ? 'proposer' : 'receiver',
    otherUser: tradeUserFromId(bulkCache, otherUserId),
    youGive,
    youReceive,
    stickersOffered: t.offered || [],
    stickersRequested: t.requested || [],
    status: t.status,
    createdAt: t.createdAt,
    resolvedAt: t.resolvedAt,
    completedAt: t.completedAt,
  };
}

/**
 * Shared hook that fetches incoming/outgoing/active trades + history.
 * Polls every 30s + on visibility change. Reused by /reservations and /album/dashboard.
 */
export function useAlbumTrades({ bulkCache: externalBulkCache } = {}) {
  const { user } = useAuth();
  const myUserId = user?.id ?? null;

  const [bulkCache, setBulkCache] = useState(() => externalBulkCache ?? loadBulkCache());
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [active, setActive] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const contactsRef = useRef(new Map());
  const [, forceContactsTick] = useState(0);

  useEffect(() => {
    if (externalBulkCache) setBulkCache(externalBulkCache);
  }, [externalBulkCache]);

  const refresh = useCallback(async () => {
    try {
      const [inRes, outRes, activeRes, historyRes] = await Promise.all([
        albumService.getIncomingTrades(),
        albumService.getOutgoingTrades(),
        albumService.getActiveTrades(),
        albumService.getTradeHistory(),
      ]);
      setIncoming(Array.isArray(inRes.data) ? inRes.data : []);
      setOutgoing(Array.isArray(outRes.data) ? outRes.data : []);
      setActive(Array.isArray(activeRes.data) ? activeRes.data : []);
      setHistory(Array.isArray(historyRes.data) ? historyRes.data : []);
    } catch {
      // keep prior data on transient error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const id = setInterval(refresh, POLL_INTERVAL_MS);
    const onVis = () => { if (document.visibilityState === 'visible') refresh(); };
    // Otras instancias del hook (ej. AlbumTradesPanel del sidebar) tienen su
    // propio state. Cuando alguien envía/acepta/rechaza un trade desde otra
    // ubicación dispatchea album:tradesChanged y refrescamos sin esperar al
    // poll de 30s.
    const onChanged = () => refresh();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('album:tradesChanged', onChanged);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('album:tradesChanged', onChanged);
    };
  }, [refresh]);

  const mappedIncoming = useMemo(
    () => myUserId === null ? [] : incoming.map(t => mapTrade(t, myUserId, bulkCache)),
    [incoming, myUserId, bulkCache]
  );
  const mappedOutgoing = useMemo(
    () => myUserId === null ? [] : outgoing.map(t => mapTrade(t, myUserId, bulkCache)),
    [outgoing, myUserId, bulkCache]
  );
  const mappedActive = useMemo(
    () => myUserId === null ? [] : active.map(t => mapTrade(t, myUserId, bulkCache)),
    [active, myUserId, bulkCache]
  );
  const mappedHistory = useMemo(
    () => myUserId === null ? [] : history.map(t => mapTrade(t, myUserId, bulkCache)),
    [history, myUserId, bulkCache]
  );

  const fetchContact = useCallback(async (tradeId) => {
    if (contactsRef.current.has(tradeId)) return contactsRef.current.get(tradeId);
    try {
      const res = await albumService.getTradeContact(tradeId);
      const contact = {
        phone: res.data?.phone ?? null,
        email: res.data?.email ?? null,
      };
      contactsRef.current.set(tradeId, contact);
      forceContactsTick(x => x + 1);
      return contact;
    } catch {
      contactsRef.current.set(tradeId, { phone: null, email: null });
      forceContactsTick(x => x + 1);
      return { phone: null, email: null };
    }
  }, []);

  const getContact = useCallback((tradeId) => contactsRef.current.get(tradeId) ?? null, []);

  return {
    loading,
    incomingTrades: mappedIncoming,
    outgoingTrades: mappedOutgoing,
    activeTrades: mappedActive,
    historyTrades: mappedHistory,
    refresh,
    fetchContact,
    getContact,
    bulkCache,
    setBulkCache,
    myUserId,
  };
}

export default useAlbumTrades;
